import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomerGroupToken, CustomerToken, EnumToken } from '@sleed/bizcommerce-api';
import { customerGroupsApi, customersApi } from '~/lib/api/client';
import { formToToken, type CustomerGroupFormValues } from '~/lib/schemas/customer-group';

export const customerGroupKeys = {
	all: ['customer-groups'] as const,
	list: () => [...customerGroupKeys.all, 'list'] as const,
	expression: (id: number) => [...customerGroupKeys.all, 'expression', id] as const,
	members: (exprId: number) => [...customerGroupKeys.all, 'members', exprId] as const
};

/** Customer groups list (also used by the banner targeting multi-select). */
export function useCustomerGroupsList() {
	return useQuery<CustomerGroupToken[]>({
		queryKey: customerGroupKeys.list(),
		queryFn: async () => (await customerGroupsApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useCustomerGroupMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: customerGroupKeys.list() });
	const create = useMutation({
		mutationFn: async (form: CustomerGroupFormValues) => (await customerGroupsApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: CustomerGroupFormValues) => (await customerGroupsApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	return { create, update };
}

/** The dynamic membership criteria (Expression JSON) for a group. */
export function useCustomerGroupExpression(id: number | null) {
	return useQuery<unknown>({
		queryKey: customerGroupKeys.expression(id ?? 0),
		queryFn: async () => (await customerGroupsApi.getExpression(id as number)).data.Expression ?? {},
		enabled: id != null && id > 0
	});
}

export function useSetCustomerGroupExpression() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, expression }: { id: number; expression: unknown }) =>
			(await customerGroupsApi.setExpression(expression, id)).data,
		onSuccess: (_d, { id }) => {
			void qc.invalidateQueries({ queryKey: customerGroupKeys.expression(id) });
			void qc.invalidateQueries({ queryKey: customerGroupKeys.list() });
		}
	});
}

/** Customers matching a group's expression (members preview). */
export function useCustomersInGroup(exprId: number | null) {
	return useQuery<CustomerToken[]>({
		queryKey: customerGroupKeys.members(exprId ?? 0),
		queryFn: async () => (await customerGroupsApi.getCustomersByExpressionId(exprId as number)).data.items ?? [],
		enabled: exprId != null && exprId > 0
	});
}

/** Gender enum options ({ id, nm }) for the customerGender criteria leaf. */
export function useCustomerGenders() {
	return useQuery<EnumToken[]>({
		queryKey: ['customer-genders'],
		queryFn: async () => (await customersApi.getGenders()).data.items ?? [],
		staleTime: 30 * 60 * 1000
	});
}

/** Hydrate customer chips by id (for the customerId criteria leaf). */
export function useCustomersByIds(ids: number[]) {
	return useQuery<CustomerToken[]>({
		queryKey: ['customers', 'by-ids', [...ids].sort((a, b) => a - b)],
		queryFn: async () => (await customersApi.getByIds(ids)).data ?? [],
		enabled: ids.length > 0
	});
}

/** Async customer search for the customerId criteria leaf. */
export async function lookupCustomers(term: string): Promise<CustomerToken[]> {
	return (await customersApi.lookup(term || undefined, 20)).data ?? [];
}
